import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { Row, Button, Column } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { CheckIcon, ClockIcon, UserIcon } from 'components/Icons';
import { ContentCard, ProjectLink, CreatedBy } from '../components';

import { useToast } from 'providers/Toast';
import { useLocalPendingEntries } from 'providers/PendingActions';
import { useProjectUpdate } from 'providers/Project';
import { useMultiStateV2 } from 'hooks';

import { MESSAGES } from '../utils';

type LoadingControl = Record<string, boolean>;

export function ProjectStartAuthorization() {
  const { t } = useLang();
  const { addToast } = useToast();

  const [loadingControl, { dispatch: changeLoading }] = useMultiStateV2<LoadingControl>({});

  const updateProject = useProjectUpdate();

  const projectsToAuthorize = useLocalPendingEntries('PROJECT_START_AUTHORIZATION');

  const authorizeStart = actions.projects.authorizeProjectStart.use({
    onSuccess: (_, { id }) => {
      updateProject(id, { authorizedToStart: true });

      addToast(MESSAGES.startAuthorizationSuccess(t));
    },

    onError: (error) => {
      if (error.message.includes('Project is already authorized to start'))
        return addToast(MESSAGES.ProjectAlreadyAuthorized(t));

      addToast(MESSAGES.startAuthorizationFail(t));
    },
  });

  const handleSaveClick = useCallback(
    async (projectId: string) => {
      changeLoading({ [projectId]: true });

      const createdProject = await authorizeStart.execute({ id: projectId });

      changeLoading({ [projectId]: false });

      if (!createdProject) return;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authorizeStart.execute, changeLoading],
  );

  return (
    <>
      {projectsToAuthorize.map((project) => (
        <ContentCard
          key={project.id}
          header={
            <Row justifyContent="space-between" fullWidth className="small_tablet:flex-col">
              <Row gap="2">
                <CreatedBy id={project.createdBy} />

                <ProjectLink id={project.id} text={project.name} />
              </Row>

              <Row className="text-sm font-medium text-primary-foreground" gap="2">
                <ClockIcon size={14} />
                {t('estimated-working-days')}: {project.estimatedCompletionWorkingDays}
              </Row>
            </Row>
          }
          content={
            <>
              <Row justifyContent="space-between" fullWidth className="small_tablet:flex-col">
                <Column>
                  <PropertyWithIcon
                    icon={<UserIcon />}
                    name={t('client')}
                    value={project.clientName}
                  />
                  <PropertyWithIcon
                    icon={<UserAvatar user={project.owner} size={30} />}
                    name={t('owner')}
                    value={project.owner.name}
                  />
                </Column>

                <Button
                  variant="outlined"
                  onClick={() => handleSaveClick(project.id)}
                  loading={loadingControl[project.id]}
                  color="success"
                  className="self-start small_tablet:mt-2 small_tablet:self-center"
                >
                  <CheckIcon size={20} />
                  {t('authorize-project-to-start')}
                </Button>
              </Row>
            </>
          }
        />
      ))}
    </>
  );
}
